import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { UtilService } from '../../../../shared/services/util.service';
import { FormaRecebimentoService } from '../../../../shared/services/apis/financas/forma-recebimento.service';
import { FormaRecebimento } from '../../../../shared/services/models/financas/forma-recebimento.model';


@Component({
    selector: 'app-formas-recebimento-list',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatCheckboxModule,
        MatMenuModule,
        MatTooltipModule,
        MatSortModule,
        MatProgressSpinnerModule,
        MatDialogModule,
        EmptyListComponent,
        RouterModule,
        HeaderComponent,
        LoadingBoxDefaultListComponent
    ],
    templateUrl: './formas-recebimento-list.component.html',
    styleUrl: './formas-recebimento-list.component.css'
})
export class FormasRecebimentoListComponent implements OnInit, OnDestroy {
    public moduloEnum = ModuloEnum.Configuracoes;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;

    public model: FormGroup;
    public _paginaAtual: number = 1;
    public _tamanhoPagina: number = 20;
    public _totalRegistros: number = 0;
    public _dataSource = new DataSourceOrdenacaoInsensivel<FormaRecebimento>();
    public _isLoading: boolean = false;

    public displayedColumns: string[] = [
        'select',
        'descricao',
        'status',
        'favorito',
        'actions'
    ];

    public selection = new SelectionModel<FormaRecebimento>(true, []);

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this._dataSource.sort = sort;
    }

    constructor(
        private formBuilder: FormBuilder,
        private alertService: AlertService,
        private dialog: MatDialog,
        private utilService: UtilService,
        private formaRecebimentoService: FormaRecebimentoService
    ) {
        this._dataSource.sortingDataAccessor = (item, property) => {
            const value = (item as any)[property];
            return typeof value === 'string' ? value.toLowerCase() : value;
        };
    }

    ngOnInit(): void {
        this.model = this.formBuilder.group({
            pesquisa: ['']
        });

        this.loadFilters();
        this.carregarDados(true);
    }

    ngOnDestroy(): void {
        this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
            '/configuracoes/financas/formas-recebimento/cadastrar',
            '/configuracoes/financas/formas-recebimento/cadastrar/'
        ]);
    }

    private loadFilters(): void {
        const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
        if (filtroSalvo) {
            this.model.patchValue(filtroSalvo);
        }
    }

    public carregarDados(reset: boolean = false): void {
        this._isLoading = true;

        if (reset) {
            this._paginaAtual = 1;
            this.selection.clear();
            this._dataSource.data = [];
        }

        const pesquisa = this.model?.get('pesquisa')?.value || '';

        this.formaRecebimentoService.get(this._paginaAtual, this._tamanhoPagina, pesquisa).subscribe({
            next: (res) => {
                this._isLoading = false;
                if (res.isError) {
                    this.alertService.show('Atenção', res.errorDescription || 'Erro ao carregar formas de recebimento.', AlertType.warning);
                    return;
                }

                this._totalRegistros = res.totalRegistros;
                const items = res.listaFormaRecebimento || [];

                if (this._paginaAtual === 1) {
                    this._dataSource.data = items;
                } else {
                    this._dataSource.data = [...this._dataSource.data, ...items];
                }
            },
            error: () => {
                this._isLoading = false;
                this.alertService.show('Erro', 'Erro ao carregar formas de recebimento.', AlertType.error);
            }
        });
    }

    public proximaPagina(): void {
        this._paginaAtual++;
        this.carregarDados();
    }

    public clickPesquisar(): void {
        this.carregarDados(true);
    }

    public limparFiltros(): void {
        this.model.patchValue({ pesquisa: '' });
        this.clickPesquisar();
    }

    public isAllSelected(): boolean {
        const selectableRows = this._dataSource.data.filter(row => !row.isPadrao);
        const numSelected = this.selection.selected.filter(row => !row.isPadrao).length;
        return numSelected === selectableRows.length && selectableRows.length > 0;
    }

    public isToggleAllDisabled(): boolean {
        return this._dataSource.data.length === 0 || this._dataSource.data.every(row => row.isPadrao);
    }

    public toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }
        this.selection.select(...this._dataSource.data.filter(row => !row.isPadrao));
    }

    public isSelecaoBloqueada(row: FormaRecebimento): boolean {
        return row.isPadrao;
    }

    public getTooltipSelecaoBloqueada(row: FormaRecebimento): string {
        if (row.isPadrao) {
            return 'Formas padrão não podem ser excluídas.';
        }
        return '';
    }

    public getTooltipExcluir(row: FormaRecebimento): string {
        return this.getTooltipSelecaoBloqueada(row);
    }

    public abrirModalExcluir(item?: FormaRecebimento): void {
        const isBulk = !item;
        const itemsToDelete = isBulk ? this.selection.selected : [item!];

        if (itemsToDelete.length === 0) {
            return;
        }

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar exclusão',
                description: isBulk
                    ? 'Confirma a exclusão das formas de recebimento selecionadas?'
                    : `Confirma a exclusão da forma de recebimento "${item!.descricao}"?`,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result?.confirm) {
                return;
            }

            this._isLoading = true;

            const delete$ = isBulk
                ? this.formaRecebimentoService.bulkDelete(itemsToDelete.map(i => i.id))
                : this.formaRecebimentoService.delete(itemsToDelete[0].id);

            delete$.subscribe({
                next: (res) => {
                    this._isLoading = false;
                    if (res.isError) {
                        this.alertService.show('Atenção', res.errorDescription || 'Erro ao excluir forma de recebimento.', AlertType.warning);
                        return;
                    }
                    this.selection.clear();
                    this.alertService.show('Sucesso',
                        isBulk
                            ? 'Formas de recebimento excluídas com sucesso.'
                            : `Forma de recebimento "${itemsToDelete[0].descricao}" excluída com sucesso.`,
                        AlertType.success);
                    this.carregarDados(true);
                },
                error: () => {
                    this._isLoading = false;
                    this.alertService.show('Erro', 'Erro ao excluir forma de recebimento.', AlertType.error);
                }
            });
        });
    }

    public definirComoFavorito(item: FormaRecebimento): void {
        if (item.isFavorito) return;

        this._isLoading = true;
        this.formaRecebimentoService.setFavorito(item.id).subscribe({
            next: (res) => {
                this._isLoading = false;
                if (res.isError) {
                    this.alertService.show('Atenção', res.errorDescription || 'Erro ao definir como favorito.', AlertType.warning);
                    return;
                }

                this._dataSource.data = this._dataSource.data.map(f => ({
                    ...f,
                    isFavorito: f.id === item.id
                }));

                this.alertService.show('Sucesso', `"${item.descricao}" definida como favorita.`, AlertType.success);
            },
            error: () => {
                this._isLoading = false;
                this.alertService.show('Erro', 'Erro ao definir como favorito.', AlertType.error);
            }
        });
    }

    public getStatusClass(isAtivo: boolean): string {
        return isAtivo ? 'badge-verde' : 'badge-cinza';
    }

}
